import { useEffect, useState } from 'react';
import TableContainer from '../../../../components/table-container/table-container';
import { TableHead } from '../../../../components/table-head';
import { useGetUsersForOgmaManagementDashboardQuery } from '../../../../slices/userApiSlice';
import { LoadingSceletonUsers } from '../../../tournaments/routes/all/loading-sceleton-users';
import { UserModel } from '../../../../features/listview/users/model/user.model';
import React from 'react';
import TableTd from '../../../../components/table-td/table-td';
import { appendAutoTags } from '../../../../utils/utils';
import { ManualTagPrefix, ProgramTitles, UserRoles } from '../../../../utils/constants';
import { useSearchGameByNameMutation } from '../../../../slices/gameApiSlice';
import { Search } from '../../../../components/search/search';
import { Link, Outlet, useSearchParams } from 'react-router-dom';
import DropdownItem from '../../../../features/listview/DropDown/DropDownItem';
import { HeaderButton } from '../../../../components/header-button';
import GenerateInviteLink from '../../../../components/generate-invite-link';
import ResendInviteLink from '../../../../components/resend-invite-link';
import { AiFillDelete } from 'react-icons/ai';
import CanIView from '../../../../components/can-i-view/can-i-view';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../../slices/auth';
import { init } from '@sentry/browser';

const OgmaDashboardStudents = () => {
  const curUser = useSelector(selectUser);

  const { data: userData, isLoading } = useGetUsersForOgmaManagementDashboardQuery({});
  const [searchGameByName, { data: gameData, isLoading: gameIsLoading, isError: gameIsError }] =
    useSearchGameByNameMutation();
  const initialOpenListState = {
    id: null,
    y: null,
    x: null,
  };
  const [search, setSearch] = useState({ by: 'firstName', keyword: '' });
  const [openList, setOpenList] = useState<{ [key: string]: number | null }>(initialOpenListState);
  const [openListView, setOpenListView] = useState(false);
  useEffect(() => {
    searchGameByName(ProgramTitles.DroneRacing);
  }, [searchGameByName]);

  function handleClick(prop: { id: number; x: number; y: number }) {
    setOpenListView(false);
    if (openList.id == prop.id) {
      setOpenList(initialOpenListState);
    } else {
      setOpenList(prop);
    }
  }

  const processedUsers = userData
    ?.filter((user: any) => {
      const model: { [key: string]: any } & UserModel = new UserModel(user);
      if (search.by === 'tags') {
        model.tags = appendAutoTags(
          (model.tags?.split(',') || ['']).map((tag) => ManualTagPrefix + tag),
          user?.team?.filter((team: any) => !team.isArchived),
          null,
          gameData?.[0]?.id,
        ).join(',');
      }
      const searchValue = model[search.by]?.toString().toLowerCase();
      return (
        (search.keyword === '' || searchValue?.includes(search.keyword.toLowerCase())) &&
        !Boolean(model.acceptedInvite)
      );
    })
    .sort((a: UserModel, b: UserModel) => {
      const modelA: { [key: string]: any } & UserModel = new UserModel(a);
      const modelB: { [key: string]: any } & UserModel = new UserModel(b);
      if (modelA.roleNumber < modelB.roleNumber) return -1;
      if (modelA.roleNumber > modelB.roleNumber) return 1;
      if (modelA[search.by] < modelB[search.by]) return -1;
      if (modelA[search.by] > modelB[search.by]) return 1;
      return 0;
    });

  const lastGMIndex = processedUsers
    ?.map((user: any, i: number) => ({ index: i, model: new UserModel(user) }))
    .filter(({ model }: any) => model.roleNumber === UserRoles.Ogma)
    .map(({ index }: any) => index)
    .pop();

  const invitedUserColumns = [
    { name: 'First Name', active: true, property: 'firstName' },
    { name: 'Last Name', active: true, property: 'lastName' },
    { name: 'Email', active: true, property: 'email' },
    { name: 'Invite Sent', active: true, property: 'updateDifference' },
    { name: 'school', active: true, property: 'school_name' },
  ];

  const configureListView = [
    {
      name: 'First Name',
      active: true,
      property: 'firstName',
    },
    {
      name: 'Last Name',
      active: true,
      property: 'lastName',
    },
    {
      name: 'Fenworks Username',
      active: true,
      property: 'username',
    },
    {
      name: 'Email Address',
      active: true,
      property: 'email',
    },
    {
      name: 'Role',
      active: true,
      property: 'role',
    },
    {
      name: 'Organization',
      active: false,
      property: 'organization',
    },
    {
      name: 'School',
      active: false,
      property: 'school_name',
    },
    {
      name: 'Tags',
      active: false,
      property: 'tags',
    },

    {
      name: 'Participation Status',
      active: false,
      property: 'status',
    },
    {
      name: 'Eligibility Status',
      active: false,
      property: 'eligibility_status',
    },

    {
      name: 'Created Date',
      active: true,
      property: 'createdAt',
    },
  ];

  const searchableColumnsList = [
    { title: 'First Name', property: 'firstName' },
    { title: 'Last Name', property: 'lastName' },
    { title: 'Username', property: 'username' },
    { title: 'Email', property: 'email' },
    { title: 'Tags', property: 'tags' },
  ];

  const invitedUsers = userData?.filter((user: any) => user.accepted_invite);

  const [searchQuery, setSearchQuery] = useState(searchableColumnsList[0].title);
  const [searchParams, setSearchParams] = useSearchParams();
  const [listView, setListView] = useState(configureListView);

  const headerButtons = [
    {
      type: 'configureListView',
      view: true,
      listView,
      setListView,
      initialListView: configureListView,
    },
  ];

  if (isLoading) return <LoadingSceletonUsers />;

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <Search
          setQuery={setSearchQuery}
          filterList={searchableColumnsList}
          search={search}
          setSearch={setSearch}
        />
        <div className='flex gap-2 mb-4 flex-wrap'>
          {headerButtons.map((b, i) => (
            <HeaderButton key={b.type} {...b} />
          ))}
          <Link to='./invite-students' className='button hover:bg-opacity-80'>
            Invite Students
          </Link>
        </div>
      </div>

      {Boolean(invitedUsers?.length) && (
        <div>
          <h1 className='flex justify-center text-lg font-medium text-fen-red'>
            You Have Users Who Have Not Accepted Their Invites!
          </h1>
          <div className='my-4 border-2 border-fen-red rounded-lg p-4'>
            <TableContainer>
              <TableHead
                oneMore
                tableList={invitedUserColumns
                  .map((l) => l.name)
                  .filter(
                    (t) =>
                      invitedUserColumns.find((d) => d.name.toLowerCase() === t.toLowerCase())
                        ?.active,
                  )}
              />
              <tbody className='bg-white divide-y divide-gray-200 '>
                {invitedUsers?.map((user: any) => {
                  const model: { [key: string]: any } & UserModel = new UserModel(user);
                  return (
                    <tr key={user.id}>
                      {invitedUserColumns
                        .filter((l) => l.active)
                        .map((t) => {
                          const model: UserModel & { [key: string]: any } = new UserModel(user);
                          if (t?.name.split(' ').join('') === 'invitesent') {
                            return (
                              <TableTd key={t.name}>
                                <span
                                  className={`basis-[20%] flex-1 font-bold text-center min-w-[1px] ${
                                    Number(model[t.property]?.split('d')?.join('')) > 7
                                      ? 'text-red-700'
                                      : ''
                                  }`}
                                >
                                  {model[t.property]}
                                </span>
                              </TableTd>
                            );
                          }
                          return <TableTd key={t.name}>{model[t.property]}</TableTd>;
                        })}
                      <TableTd>
                        <div className='flex items-center gap-2'>
                          <div className='flex  items-center gap-1'>
                            <CanIView component='generateInviteLink'>
                              <GenerateInviteLink email={user?.email} />
                            </CanIView>
                            <ResendInviteLink email={model.email} extraClassName='h-9 p-4' />
                          </div>
                          <div>
                            <Link to={`cancel/${user?.id}`} className='cursor-pointer'>
                              <AiFillDelete color='red' size={30} />
                            </Link>
                          </div>
                        </div>
                      </TableTd>
                    </tr>
                  );
                })}
              </tbody>
            </TableContainer>
          </div>
        </div>
      )}
      <div>
        <h1 className='font-bold text-xl'>Users</h1>
        <TableContainer>
          <TableHead
            tableList={listView
              .map((l) => l.name)
              .filter(
                (t) => listView.find((d) => d.name.toLowerCase() === t.toLowerCase())?.active,
              )}
            bool={true}
          />
          <tbody className='bg-white divide-y divide-gray-200'>
            {processedUsers.map((user: any, i: number) => {
              const model: { [key: string]: any } & UserModel = new UserModel(user);

              return (
                <React.Fragment key={model.id}>
                  {
                    <tr
                      key={model.email}
                      className='border-b-2 border-dashed border-gray-500'
                      onClick={(e) => {
                        if (model.roleNumber === UserRoles.Student)
                          handleClick({ id: user.id, x: e.pageX, y: e.pageY });
                      }}
                    >
                      {listView
                        .filter((l) => l.active)
                        .map((p) => {
                          if (p.property === 'tags') {
                            return (
                              <TableTd key={p.property}>
                                {typeof model.tags !== 'undefined' ? (
                                  appendAutoTags(
                                    (model.tags?.split(',') || ['']).map(
                                      (tag) => ManualTagPrefix + tag,
                                    ),
                                    user?.team?.filter((team: any) => !team.isArchived),
                                    null,
                                    gameData?.[0]?.id,
                                  ).map((t: string, i: number) => (
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Non auto generated tags have a "#" prefix
                                        let isGame;
                                        [t, isGame] = t.startsWith(ManualTagPrefix)
                                          ? [t.slice(1), 'false']
                                          : [t, 'true'];
                                        setSearch((s) =>
                                          s.by === 'tags' && s.keyword === t
                                            ? {
                                                by: searchableColumnsList[0].property,
                                                keyword: '',
                                              }
                                            : { by: 'tags', keyword: t },
                                        );
                                        setSearchQuery('tags');
                                        setSearchParams({
                                          tags: t,
                                          isGame,
                                        });
                                        search.by === 'tags' &&
                                          search.keyword === t &&
                                          setSearchParams();
                                      }}
                                      key={t + i}
                                      className={`tag ${(() => {
                                        const tagNoPrefix = t.startsWith(ManualTagPrefix)
                                          ? t.slice(1)
                                          : t;
                                        return search.by === 'tags' &&
                                          search.keyword === tagNoPrefix
                                          ? 'active'
                                          : '';
                                      })()}`}
                                    >
                                      {t}
                                    </span>
                                  ))
                                ) : (
                                  <>{'-'}</>
                                )}
                              </TableTd>
                            );
                          }
                          if (p.property === 'username')
                            return (
                              <TableTd key={p.property} extraClass='max-w-[15ch]'>
                                <div className='text-sm font-medium text-gray-900 truncate'>
                                  {model.username}
                                </div>
                              </TableTd>
                            );
                          if (p.property === 'organization') {
                            return (
                              <TableTd key={p.property} extraClass='max-w-[15ch]'>
                                <div className='text-sm font-medium text-gray-900 truncate'>
                                  {Array.isArray(model.organization)
                                    ? model.organization.map((org, index) => (
                                        <span key={index}>
                                          {index > 0 && ', '}
                                          {typeof org === 'object' ? JSON.stringify(org) : org}
                                        </span>
                                      ))
                                    : model.organization}
                                </div>
                              </TableTd>
                            );
                          }
                          if (p.property === 'firstName')
                            return (
                              <TableTd key={p.property} extraClass='max-w-[15ch]'>
                                <div className='text-sm font-medium text-gray-900 truncate'>
                                  {model.firstName}
                                </div>
                              </TableTd>
                            );
                          if (p.property === 'lastName')
                            return (
                              <TableTd key={p.property} extraClass='max-w-[15ch]'>
                                <div className='text-sm font-medium text-gray-900 truncate'>
                                  {model.lastName}
                                </div>
                              </TableTd>
                            );
                          if (p.property === 'school_name')
                            return (
                              <TableTd key={p.property} extraClass='max-w-[15ch]'>
                                <div className='text-sm font-medium text-gray-900 truncate'>
                                  {model.school_name}
                                </div>
                              </TableTd>
                            );

                          if (p.property.includes('status'))
                            return (
                              <TableTd key={p.property}>
                                <span
                                  className={`invoiceStatus ${model[p.property]?.toLowerCase()}`}
                                >
                                  {model[p.property]}
                                </span>
                              </TableTd>
                            );
                          return <TableTd key={p.property}>{model[p.property]}</TableTd>;
                        })}
                      <TableTd>
                        <div className='listViewMenu--parent'>
                          <div
                            className={openList.id === model.id ? '' : 'hidden'}
                            style={
                              openList.id === model.id
                                ? {
                                    position: 'fixed',
                                    top: Number(openList.y) - window.scrollY,
                                    left:
                                      Number(openList.x) - window.scrollX < 200
                                        ? Number(openList.x) - window.scrollX + 200
                                        : Number(openList.x) - window.scrollX,
                                    zIndex: 99,
                                  }
                                : {
                                    left: 0,
                                  }
                            }
                          >
                            <div
                              className='listViewMenu'
                              role='menu'
                              aria-orientation='vertical'
                              aria-labelledby='menu-button'
                            >
                              <div className='' role='none'>
                                <DropdownItem
                                  justView={curUser.roles === UserRoles.Ogma}
                                  {...(model.roleNumber === UserRoles.Admin ||
                                  model.roleNumber === UserRoles.Fan
                                    ? { noView: true }
                                    : {
                                        viewLink: `/users/view/${model.id}/${model.role}`,
                                      })}
                                  hasDelete={curUser.roles === UserRoles.Admin}
                                  hasEdit={curUser.roles === UserRoles.Admin}
                                  id={model.id}
                                  setFunc={setOpenList}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </TableTd>
                    </tr>
                  }
                </React.Fragment>
              );
            })}
          </tbody>
        </TableContainer>
      </div>
      <Outlet />
    </div>
  );
};

export default OgmaDashboardStudents;
